import { Component, EventEmitter, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import {
  DxTextAreaModule,
  DxNumberBoxModule,
  DxSwitchModule,
  DxDateBoxModule,
  DxButtonModule,
  DxLoadPanelModule,
} from 'devextreme-angular';
import notify from 'devextreme/ui/notify';
import { ConfigService } from '../../services/config.service';

/** The control rendered for a setting. Inferred from the stored value (the config table has no type column). */
type ControlType = 'boolean' | 'number' | 'date' | 'text';

/** A config row plus the working value bound to its control + the original string (for dirty tracking). */
interface EditableConfig {
  id?: number;
  config_key: string;
  controlType: ControlType;
  original: string;
  boolValue: boolean;
  numberValue: number | null;
  dateValue: Date | null;
  textValue: string;
}

/**
 * Configuration editor (popup content). Lists every row in the `config` table and lets the user edit
 * each value. The table stores all values as strings (config_string) with NO type column, so the control
 * for each setting is INFERRED from the value: 'true'/'false' → switch, numeric → number box, ISO date
 * (yyyy-MM-dd) → date box, anything else → multi-line text. Changed rows are saved back via the config
 * update endpoint (updateOrCreate keyed on config_key). Launched from the user profile dropdown.
 */
@Component({
  selector: 'app-config-editor',
  templateUrl: './config-editor.component.html',
  styleUrls: ['./config-editor.component.css'],
  imports: [
    CommonModule,
    DxTextAreaModule,
    DxNumberBoxModule,
    DxSwitchModule,
    DxDateBoxModule,
    DxButtonModule,
    DxLoadPanelModule,
  ],
  standalone: true,
})
export class ConfigEditorComponent {
  /** Emitted to ask the host (header popup) to close. */
  @Output() close = new EventEmitter<void>();

  configs: EditableConfig[] = [];
  loading = false;
  saving = false;
  loadError = false;

  constructor(private configService: ConfigService) {}

  /** Called by the popup's (onShown) hook — (re)loads the full config list each time the popup opens. */
  onShown(): void {
    void this.load();
  }

  private async load(): Promise<void> {
    this.loading = true;
    this.loadError = false;
    try {
      const rows = (await this.configService.getAllConfig()) ?? [];
      this.configs = rows
        .slice()
        .sort((a, b) => a.config_key.localeCompare(b.config_key))
        .map((r) => this.toEditable(r));
    } catch (e) {
      console.error('Failed to load configuration', e);
      this.loadError = true;
      this.configs = [];
    } finally {
      this.loading = false;
    }
  }

  /** Infer the control type from the stored string value (the config table has no type column). */
  controlTypeFor(raw: string | null | undefined): ControlType {
    const v = (raw ?? '').trim();
    const lower = v.toLowerCase();
    if (lower === 'true' || lower === 'false') {
      return 'boolean';
    }
    if (/^-?\d+(\.\d+)?$/.test(v)) {
      return 'number';
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
      return 'date';
    }
    return 'text';
  }

  private toEditable(r: { id?: number; config_key: string; config_string?: string }): EditableConfig {
    const raw = (r.config_string ?? '').toString();
    const controlType = this.controlTypeFor(raw);
    return {
      id: r.id,
      config_key: r.config_key,
      controlType,
      original: raw,
      boolValue: raw.trim().toLowerCase() === 'true',
      numberValue: controlType === 'number' ? Number(raw) : null,
      dateValue: controlType === 'date' ? this.parseIsoDate(raw) : null,
      textValue: raw,
    };
  }

  /** Parse a yyyy-MM-dd string into a local Date (avoids the UTC shift of `new Date(iso)`). */
  private parseIsoDate(iso: string): Date | null {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
    if (!m) {
      return null;
    }
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  }

  /** Serialise an editable row's working value back to the string stored in config_string. */
  serialize(c: EditableConfig): string {
    switch (c.controlType) {
      case 'boolean':
        return c.boolValue ? 'true' : 'false';
      case 'number':
        return c.numberValue === null || c.numberValue === undefined ? '' : String(c.numberValue);
      case 'date':
        return c.dateValue ? this.toIsoDate(c.dateValue) : '';
      default:
        return c.textValue ?? '';
    }
  }

  private toIsoDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  isDirty(c: EditableConfig): boolean {
    return this.serialize(c) !== c.original;
  }

  get hasChanges(): boolean {
    return this.configs.some((c) => this.isDirty(c));
  }

  /** Persist every changed row (one update per row), then close. The API requires non-empty values. */
  async save(): Promise<void> {
    const changed = this.configs.filter((c) => this.isDirty(c));
    if (changed.length === 0) {
      notify('No changes to save.', 'info', 2000);
      return;
    }
    const blank = changed.find((c) => this.serialize(c).trim() === '');
    if (blank) {
      notify(`"${blank.config_key}" can't be empty.`, 'error', 3000);
      return;
    }
    this.saving = true;
    try {
      await Promise.all(
        changed.map((c) =>
          firstValueFrom(this.configService.saveConfigByKey(c.config_key, this.serialize(c))),
        ),
      );
      changed.forEach((c) => (c.original = this.serialize(c)));
      notify(`Saved ${changed.length} setting${changed.length === 1 ? '' : 's'}.`, 'success', 2500);
      this.close.emit();
    } catch (e) {
      console.error('Failed to save configuration', e);
      notify('Could not save one or more settings. Please try again.', 'error', 4000);
    } finally {
      this.saving = false;
    }
  }

  cancel(): void {
    this.close.emit();
  }
}
